import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient } from '@qwickforge/react-client';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import { Button, Code, Text } from '@qwickapps/react-framework';

interface DeviceDetails {
  deviceId: string;
  deviceName: string;
  platform: string;
  registeredAt: string;
}

export default function DeviceSetupPage() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const client = useMiddlewareClient();
  const [activeStep, setActiveStep] = useState(0);
  const [pairingCode, setPairingCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);
  const [deviceRegistered, setDeviceRegistered] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState<DeviceDetails | null>(null);
  const [error, setError] = useState<string>('');
  const [codeExpired, setCodeExpired] = useState(false);
  const [projectsDir, setProjectsDir] = useState<string>('~/Projects');
  const [projectName, setProjectName] = useState<string>('');
  const [projectSlug, setProjectSlug] = useState<string>('');

  const steps = ['Install CLI', 'Configure Projects', 'Register Device'];

  // Auto-generate project slug from project name
  useEffect(() => {
    if (projectName) {
      const slug = projectName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setProjectSlug(slug);
    } else {
      setProjectSlug('');
    }
  }, [projectName]);

  // Generate pairing code when reaching step 3
  useEffect(() => {
    if (activeStep === 2 && !pairingCode) {
      generatePairingCode();
    }
  }, [activeStep, pairingCode]);

  // Poll for device registration
  useEffect(() => {
    if (activeStep === 2 && pairingCode && !deviceRegistered && !codeExpired) {
      setIsPolling(true);
      const interval = setInterval(async () => {
        const result = await checkDeviceRegistration(pairingCode);
        if (result.registered) {
          setDeviceRegistered(true);
          setIsPolling(false);
          clearInterval(interval);
          // Fetch device details
          await fetchDeviceDetails(pairingCode);
        } else if (result.expired) {
          setCodeExpired(true);
          setIsPolling(false);
          setError('Pairing code has expired. Please generate a new code.');
          clearInterval(interval);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [activeStep, pairingCode, deviceRegistered, codeExpired]);

  const generatePairingCode = async () => {
    try {
      setError('');
      setCodeExpired(false);

      // Wait for auth to initialize
      if (authLoading) {
        return;
      }

      if (!session?.accessToken || !client) {
        setError('Not authenticated. Please sign in first.');
        return;
      }

      const data = await client.pairing.generateCode();
      setPairingCode(data.code);
      setExpiresAt(data.expires_at);
    } catch (error) {
      console.error('Failed to generate pairing code:', error);
      setError('Failed to connect to server. Please try again.');
    }
  };

  const checkDeviceRegistration = async (code: string): Promise<{ registered: boolean; expired?: boolean }> => {
    if (!client) return { registered: false };

    try {
      const data = await client.pairing.checkStatus(code);
      return {
        registered: data.registered === true,
        expired: data.expired === true,
      };
    } catch (error) {
      console.error('Failed to check device registration:', error);
      return { registered: false };
    }
  };

  const fetchDeviceDetails = async (code: string) => {
    if (!client) return;

    try {
      // In a real implementation, you'd fetch device details from an endpoint
      // For now, we'll simulate it
      const data = await client.pairing.checkStatus(code);
      if (data.deviceDetails) {
        setDeviceDetails(data.deviceDetails);
      }
    } catch (error) {
      console.error('Failed to fetch device details:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1 && deviceRegistered) {
      navigate('/dashboard');
    } else if (activeStep === 1) {
      // Save projects directory and update default project
      try {
        await client.profile.update({ projectsDir });

        // Get user profile to find default project
        const profile = await client.profile.get();

        if (profile.defaultProjectId) {
          // Update the default project with user's chosen name and slug
          await client.projects.update(profile.defaultProjectId, {
            name: projectName,
            slug: projectSlug,
            description: `My first project`,
          });
        }

        setActiveStep((prev) => prev + 1);
      } catch (error) {
        console.error('Failed to save configuration:', error);
        setError('Failed to save configuration. Please try again.');
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRegenerateCode = () => {
    setPairingCode('');
    setCodeExpired(false);
    setError('');
    generatePairingCode();
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8 }}>
        <Text variant="h4" gutterBottom customColor="var(--theme-text-primary)" content="Device Setup" />
        <Text variant="body1" customColor="var(--theme-secondary)" content="Add your first device to start running AI agents" sx={{ mb: 4 }} />

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 4 }}>
          {activeStep === 0 && (
            <Box>
              <Text variant="h6" gutterBottom content="Step 1: Install QwickAI CLI" />
              <Text variant="body2" customColor="var(--theme-secondary-variant)" content="Install the CLI on the device where you want to run AI agents" sx={{ mb: 3 }} />

              <Code title="macOS / Linux">
                curl -fsSL https://qwickforge.com/downloads/install.sh | sh
              </Code>
              <Box sx={{ my: 2, textAlign: 'center' }}>or</Box>
              <Code title="Windows">
                iwr https://qwickforge.com/downloads/install.ps1 | iex
              </Code>

              <Alert severity="info" sx={{ mb: 3, mt: 4 }}>
                After installation, verify by running: <code>qwickai --version</code>
              </Alert>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Text variant="h6" gutterBottom content="Step 2: Configure Your Projects Directory" />
              <Text variant="body2" customColor="var(--theme-secondary-variant)" content="Set up where your projects will be stored and create your first project" sx={{ mb: 3 }} />

              {error && (
                <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Text variant="subtitle2" gutterBottom content="Projects Directory" />
                <TextField
                  fullWidth
                  value={projectsDir}
                  onChange={(e) => setProjectsDir(e.target.value)}
                  placeholder="~/Projects"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon sx={{ color: 'var(--theme-secondary)' }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Base directory where all your projects will be created"
                  sx={{ mb: 3 }}
                />

                <Text variant="subtitle2" gutterBottom content="First Project Name" />
                <TextField
                  fullWidth
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="My First Project"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FolderIcon sx={{ color: 'var(--theme-secondary)' }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Name for your first project"
                  sx={{ mb: 2 }}
                />

                {projectSlug && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Project folder: <code>{projectsDir}/{projectSlug}</code>
                  </Alert>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Text variant="h6" gutterBottom content="Step 3: Register Your Device" />

              {error && (
                <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {!deviceRegistered && pairingCode && !codeExpired && (
                <>
                  <Text variant="body2" customColor="var(--theme-surface)" content="Use this pairing code to register your device. The code expires in 10 minutes." sx={{ mb: 2 }} />

                  <Card sx={{ mb: 3, bgcolor: 'var(--theme-surface)' }}>
                    <CardContent>
                      <Text variant="overline" customColor="var(--theme-on-surface)" content="Pairing Code" />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Text variant="h3" customColor="var(--theme-on-surface)" content={pairingCode} sx={{ fontFamily: 'monospace', letterSpacing: '0.2em' }} />
                        <Button
                          size="small"
                          icon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(pairingCode)}
                        >
                          Copy
                        </Button>
                      </Box>
                      <Text variant="caption" customColor="var(--theme-secondary)" content={`Expires: ${new Date(expiresAt).toLocaleTimeString()}`} sx={{ mt: 1, display: 'block' }} />
                    </CardContent>
                  </Card>

                  <Code title="Run this command on your device:" sx={{ flex: 1, fontSize: '1rem' }}>qwickai register {pairingCode}</Code>

                  {isPolling && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <CircularProgress size={20} />
                      <Text variant="body2" customColor="var(--theme-secondary)" content="Waiting for device registration..." />
                    </Box>
                  )}
                </>
              )}

              {codeExpired && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                  <Text variant="h6" gutterBottom content="Pairing Code Expired" />
                  <Text variant="body2" customColor="var(--theme-secondary)" content="The pairing code has expired. Please generate a new code." sx={{ mb: 3 }} />
                  <Button variant="contained" onClick={handleRegenerateCode}>
                    Generate New Code
                  </Button>
                </Box>
              )}

              {deviceRegistered && (
                <Box>
                  <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
                    Device registered successfully!
                  </Alert>

                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Text variant="h6" gutterBottom content="Device Details" />
                      <Divider sx={{ my: 2 }} />
                      {deviceDetails ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text variant="body2" customColor="var(--theme-secondary)" content="Device Name:" />
                            <Text variant="body2" content={deviceDetails.deviceName} sx={{ fontFamily: 'monospace' }} />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text variant="body2" customColor="var(--theme-secondary)" content="Device ID:" />
                            <Text variant="body2" content={`${deviceDetails.deviceId.substring(0, 8)}...`} sx={{ fontFamily: 'monospace' }} />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text variant="body2" customColor="var(--theme-secondary)" content="Platform:" />
                            <Text variant="body2" content={deviceDetails.platform} />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text variant="body2" customColor="var(--theme-secondary)" content="Registered:" />
                            <Text variant="body2" content={new Date(deviceDetails.registeredAt).toLocaleString()} />
                          </Box>
                        </Box>
                      ) : (
                        <Text variant="body2" customColor="var(--theme-secondary)" content="Device is ready to use" />
                      )}
                    </CardContent>
                  </Card>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Your device is now registered and ready to run AI agents. Click "Go to Dashboard" to start using
                    QwickForge.
                  </Alert>
                </Box>
              )}

              {!deviceRegistered && !pairingCode && !error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Troubleshooting Section */}
              <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text variant="subtitle2" content="Troubleshooting" />
                </AccordionSummary>
                <AccordionDetails>
                  <Text variant="body2" gutterBottom content="CLI not found?" fontWeight="bold" />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="Make sure the CLI is installed and in your PATH. Try running: qwickai --version" sx={{ mb: 2 }} />

                  <Text variant="body2" gutterBottom content="Registration failing?" fontWeight="bold" />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="• Check that the pairing code hasn't expired (valid for 10 minutes)" sx={{ mb: 2 }} />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="• Ensure you have network connectivity" sx={{ mb: 2 }} />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="• Verify the service URL is correct in your environment" sx={{ mb: 2 }} />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="• Try generating a new pairing code" sx={{ mb: 2 }} />

                  <Text variant="body2" gutterBottom content="Need help?" fontWeight="bold" />
                  <Text variant="body2" customColor="var(--theme-surface-variant)" content="Contact support with error code if you continue to experience issues." />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 1 && (!projectsDir || !projectName)) ||
                (activeStep === 2 && !deviceRegistered)
              }
            >
              {activeStep === steps.length - 1 ? 'Go to Dashboard' : 'Next'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
